import React, { useCallback } from 'react';
import styled from 'styled-components';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab-old/Autocomplete';
import Chip from '@material-ui/core/Chip';

export interface IRefOption {
  value: number;
  label: string;
}

type Props = {
  value: number[];
  onChange: (ids: number[]) => void;
  options: IRefOption[];
  className?: string;
  label?: string;
};

const RefSelectorList: React.FC<Props> = ({ value, onChange, options, className, label = 'Select Items' }) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, selectedOptions: IRefOption[]) => {
      const selectedValues = selectedOptions.map(opt => opt.value);
      onChange(selectedValues);
    },
    [onChange],
  );

  const selectedOptions = options.filter(opt => value.includes(opt.value));

  return (
    <Wrapper className={className}>
      <AutoCompleteStyled
        multiple
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, value) => option.value === value.value}
        filterSelectedOptions  // This prop automatically filters out selected options from the dropdown
        renderInput={(params) => (
          <TextFieldStyled
            {...params}
            variant="outlined"
            label={label}
            size="small"
          />
        )}
        renderTags={(value: IRefOption[], getTagProps) =>
          value.map((option: IRefOption, index: number) => (
            <ChipStyled
              label={option.label}
              {...getTagProps({ index })}
              size="small"
            />
          ))
        }
        disableCloseOnSelect
      />
    </Wrapper>
  );
};

export default RefSelectorList;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const AutoCompleteStyled = styled(Autocomplete)`
  width: 100%;
  
  & .MuiInputBase-root {
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  & .MuiInputBase-input {
    height: auto;
    min-height: 24px;
  }
  
  & .MuiInputLabel-outlined.MuiInputLabel-marginDense {
    transform: translate(14px, 12px) scale(1);
  }
  
  & .MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: translate(14px, -6px) scale(0.75);
  }
  
  & .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
    padding-right: 65px;
  }
  
  & .MuiAutocomplete-tag {
    margin: 2px;
  }
`;

const TextFieldStyled = styled(TextField)`
  width: 100%;
`;

const ChipStyled = styled(Chip)`
  && {
    height: 24px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    
    & .MuiChip-label {
      padding-left: 8px;
      padding-right: 8px;
      font-size: 13px;
    }
    
    & .MuiChip-deleteIcon {
      font-size: 18px;
      color: #666;
      
      &:hover {
        color: #000;
      }
    }
  }
`;